<template>
	<div>
		<div v-if="!isLoading">
			<div>
				<div class="flex">
					<div class="w-full">
						<div class="flex w-full">
							<BaseSelect
								ref="BaseSelect"
								v-model="tasksChosed"
								:allow-empty="true"
								:multiple="true"
								:options="taskOptions"
								class="mr-4 w-1/2"
								:label="$t('work_steps')"
								options-label="name"
							/>
							<BaseInput v-model="customTask" class="mr-4 flex-grow" :label="$t('special_work_step')" />
							<div>
								<BaseButton :disabled="!customTask && (!tasksChosed || !tasksChosed.length)" class="mt-8" @click="add">
									<font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
									{{ $t('add') }}
								</BaseButton>
							</div>
						</div>
					</div>
				</div>

				<div class="main">
					<div v-if="repairPlan" class="mt-4 repairPlanTable" ref="repairPlanTable">
						<RepairPlanRow
							v-for="(task, index) in repairPlan.repair_plan_rows"
							:key="task.id"
							:ref="'RepairPlanRow.' + index"
							:index="index"
							:repair-plan="repairPlan"
							:task="task"
							@change="disablePdfAndDownload"
							@delete="deleteRow(index)"
							@update:date="
								task.date = $event;
								isSorted = false;
							"
							@update:time="
								task.time = $event;
								isSorted = false;
							"
						/>
					</div>
				</div>
			</div>
			<div v-if="repairPlan && repairPlan.repair_plan_rows && repairPlan.repair_plan_rows.length" class="flex justify-end pt-8">
				<BaseButton :disabled="isEmptyList" class="mr-4" @click="openRepairPlanOrder">
					<font-awesome-icon :icon="['fas', 'file-alt']" class="mr-2" />
					{{ $t('generate_order_form') }}
				</BaseButton>
				<BaseButton v-if="repairPlanOrderPdfUrl" class="mr-4" @click="downloadFile(repairPlanOrderPdfUrl)">
					<font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
					{{ $t('order_form_download') }}
				</BaseButton>
				<BaseButton :disabled="isEmptyList" class="mr-4" @click="sortRows">
					<font-awesome-icon :icon="['fas', 'sort-amount-down']" class="mr-2" />
					{{ $t('sort') }}
				</BaseButton>
				<BaseButton :disabled="!alreadyInsertedRows || isEmptyList" class="mr-4" @click="createPdf">
					<font-awesome-icon :icon="['fas', 'file-pdf']" class="mr-2" />
					{{ $t('generate_pdf') }}
				</BaseButton>
				<BaseButton v-if="!disabledDownload" class="mr-4" @click="downloadFile(repairPlanBlob)">
					<font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
					{{ $t('download') }}
				</BaseButton>
				<BaseButton :disabled="isEmptyList" class="btn-blue" @click="save">{{ $t('general.save') }}</BaseButton>
			</div>
		</div>
		<LoadingSpinnerWidget v-else />
	</div>
</template>

<script>
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseInput from '@/components/forms/inputs/BaseInput';
import RepairPlanRow from '@/components/rows/RepairPlanRow';
import apiDefaults from '@/api/defaultConfiguration';
import { PdfApiFactory, RepairplanApiFactory, RepairPlanTaskApiFactory } from '@/api/generated';
import defaultConfiguration from '@/api/defaultConfiguration';
import { getRegularDate, getUnixTimestamp } from '@/plugins/Utils';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import { PDFTypes } from '@/api/generated/models';
import ModalMixin from '@/mixins/ModalMixin';
import { default as LoadingSpinnerWidget } from '@/components/navigation/LoadingSpinner.vue';
import Authentication from '@/mixins/Authentication';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';
import PDFRepository from '@/api/repositories/PDFRepository';

const repairplanApiFactory = RepairplanApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios);

export default {
	name: 'ProcessRepairPlanModal',
	components: { LoadingSpinnerWidget, RepairPlanRow, BaseInput, BaseButton, BaseSelect },
	mixins: [LoadingSpinner, ModalMixin, Authentication, AppContainerMixin],
	data() {
		return {
			taskOptions: [],
			tasksChosed: null,
			repairPlan: null,
			repairPlanOrderPdfUrl: null,
			documentName: '',
			repairPlanBlob: null,
			customTask: null,
			newTasks: [],
			disabledPdf: true,
			disabledDownload: true,
			isLoading: true,
			isSorted: false,
		};
	},
	computed: {
		alreadyInsertedRows() {
			const rows = this.repairPlan?.repair_plan_rows?.filter(row => row.id !== undefined);
			if (rows?.length) {
				return rows;
			}
			return undefined;
		},
		isEmptyList() {
			const rows = this.repairPlan?.repair_plan_rows;
			return !rows || rows.length === 0;
		},
	},
	mounted() {
		if (!this.isUserAllowedInLevel5or6) {
			this.$emit('authNotAllowed');
		} else {
			this.isLoading = true;
			new PDFRepository()
				.getPDFFile(this.$route.params.processId, 'repairplan_order')
				.then(response => {
					new PDFDamagecasesRepository().getSavedDocument(response.data.data.uuid).then(response => {
						this.documentName = response.headers['content-disposition'].split('filename=')[1];
						this.repairPlanOrderPdfUrl = new Blob([response.data], { type: 'application/pdf' });
					});
				})
				.catch(() => {
					this.isLoading = false;
				});

			this.getRows().catch(error => {
				if (error.response?.status === 404) {
					repairplanApiFactory
						.damageCaseDamageCaseIdRepairPlanPost(this.$route.params.processId)
						.then(response => {
							this.repairPlan = { ...response.data.data };
						})
						.catch(error => {
							this.isLoading = false;
							this.$swal(this.$t('general.error').toString(), this.$t('schedule_generation_error').toString(), 'error');
						});
				}
			});
			RepairPlanTaskApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
				.repairPlanTasksGet()
				.then(response => {
					this.taskOptions = response.data.data;
					this.isLoading = false;
				})
				.catch(error => {
					this.isLoading = false;
					this.$swal(this.$t('general.error').toString(), this.$t('no_tasks_found').toString(), 'error');
				});
		}
	},
	methods: {
		downloadFile(blob, name) {
			if (this.isAppContainer) {
				const reader = new FileReader();
				const name = this.documentName;
				reader.onload = function() {
					const arrayBuffer = this.result;
					// convert the ArrayBuffer to a comma-separated string
					const dataString = new Uint8Array(arrayBuffer).join(',');
					const messageData = {
						fileName: name,
						data: dataString,
					};
					// send the data to Swift
					window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
				};
				reader.readAsArrayBuffer(blob);
			} else {
				const url = window.URL.createObjectURL(blob);
				const name = this.documentName;
				setTimeout(function() {
					const a = document.createElement('a');
					a.style.display = 'none';
					a.href = url;
					a.download = name;
					document.body.appendChild(a);
					a.click();
					window.URL.revokeObjectURL(url);
				}, 200);
			}
		},
		openRepairPlanOrder() {
			this.save(false).then(() => {
				this.$router.push({
					name: 'damagecases.repairplan.orderform',
					params: { customer: this.$route.params.customer, processId: this.$route.params.processId },
				});
			});
		},
		save(successMessage = true) {
			this.setLoadingStatus(true);
			const promise = [];
			promise.push(repairplanApiFactory.repairPlanIdRepairPlanRowBatchPost(this.repairPlan.id, this.newTasks));

			for (let i = 0; i < this.alreadyInsertedRows?.length ?? 0; i++) {
				const row = this.alreadyInsertedRows[i];
				if (row.name === '') {
					this.$swal(this.$t('general.error').toString(), this.$t('no_empties_allowed').toString(), 'error');
					this.setLoadingStatus(false);
					return;
				}
				promise.push(repairplanApiFactory.repairPlanRepairPlanIdRepairPlanRowIdPut(this.repairPlan.id, row.id, row));
			}

			return Promise.all(promise)
				.then(response => {
					if (successMessage) {
						this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
					}
					this.$store.commit('setConfirmLeaving', false);
					this.newTasks = [];
					this.getRows();
					this.setLoadingStatus(false);
					this.isLoading = false;
					this.disabledPdf = false;
					this.disabledDownload = true;
				})
				.catch(error => {
					this.isLoading = false;
					this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
					this.setLoadingStatus(false);
				});
		},
		disablePdfAndDownload() {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			this.disabledPdf = false;
			this.disabledDownload = true;
		},
		sortRows() {
			if (this.isSorted) {
				this.$swal(this.$t('general.hint').toString(), this.$t('dates_already_sorted_chronologically').toString(), 'info');
			} else {
				this.repairPlan.repair_plan_rows.sort((first, second) => {
					return first.date - second.date;
				});
				this.isSorted = true;
			}
		},
		createPdf() {
			this.setLoadingStatus(true);
			PdfApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
				.pdfDamagecasesTypeSavePost(PDFTypes.Repairplan, { id: this.$route.params.processId })
				.then(response => {
					new PDFDamagecasesRepository().getSavedDocument(response.data.uuid).then(response => {
						this.repairPlanBlob = new Blob([response.data], { type: 'application/pdf' });
					});
					this.repairPlan.pdf = response.data.uuid;
					this.$swal(this.$t('general.success').toString(), this.$t('pdf_repair_schedule_generated').toString(), 'success');
					this.$store.dispatch('updateDocuments');
					this.setLoadingStatus(false);
					this.disabledDownload = false;
				})
				.catch(error => {
					this.setLoadingStatus(false);
					this.$swal(this.$t('general.error').toString(), this.$t('pdf_generation_error').toString(), 'error');
				});
		},
		getRows() {
			return repairplanApiFactory.damagecasesDamageCaseIdRepairPlanGet(this.$route.params.processId).then(response => {
				this.repairPlan = { ...response.data.data };
				this.isSorted = true;
			});
		},
		deleteRow(index) {
			const row = this.repairPlan.repair_plan_rows.splice(index, 1);
			const newTaskIndex = this.newTasks.find(value => value.id === row.id);
			if (newTaskIndex) {
				this.newTasks.splice(newTaskIndex, 1);
			}
		},
		add() {
			this.isSorted = false;
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			//this.$refs.BaseSelect.close();
			const tasks = [];
			this.customTask ? tasks.push({ name: this.customTask, date: getUnixTimestamp(), isNew: true }) : null;

			this.tasksChosed?.forEach(value => {
				tasks.push({ name: value.name, date: getUnixTimestamp(), isNew: true });
			});

			for (let i = 0; i < tasks.length; i++) {
				this.newTasks.push(tasks[i]);
				this.repairPlan.repair_plan_rows.push(tasks[i]);
			}

			this.customTask = null;
			this.tasksChosed = null;
			this.$nextTick(() => {
				this.$nextTick(() => {
					// otherwise the UI is not updated to scroll down
					const lastIndex = this.repairPlan.repair_plan_rows.length - 1;
					const refKey = 'RepairPlanRow.' + lastIndex;
					this.$refs[refKey][0].$refs['name'].focus();
				});
			});
		},
	},
};
</script>

<style scoped>
.modalComponent .main,
.modalComponent.main {
	max-height: 60vh;
}

@media only screen and (max-width: 1181px) {
	.modalComponent .main,
	.modalComponent.main {
		min-height: 50vh;
		max-height: 50vh;
	}
}
</style>
